import { inject, Injectable } from '@angular/core';
import type {
  ISystemParam,
  ISystemParamsGridItem,
  ISystemParamUpdateParams,
} from '@core/interfaces/system-params';
import { KeycloakSessionService } from '@core/services/auth/keycloak-session.service';
import type { IGridRequestBody } from 'mt-ui-lib/interfaces';
import { catchError, defer, forkJoin, map, of } from 'rxjs';
import { settingsGridPage } from './grid-query';
import { NotificationConfigurationApiService } from './notification-configuration-api.service';
import {
  NOTIFICATION_EVENT_AGE_PARAM_ID,
  notificationEventAgeParam,
  parseMaximumEventAge,
} from './notification-system-settings-param';
import { smtpConfigurationParams } from './smtp-configuration-params';
import type { SystemParamsDataSource } from './system-params-data-source';

@Injectable({ providedIn: 'root' })
export class NotificationSettingsApiService implements SystemParamsDataSource {
  public readonly source = 'notifications';
  private readonly notifications = inject(NotificationConfigurationApiService);
  private readonly session = inject(KeycloakSessionService);

  public getGrid(body: IGridRequestBody) {
    return this.rows().pipe(
      map((rows) =>
        settingsGridPage(
          rows.map<ISystemParamsGridItem & Pick<ISystemParam, 'isProtected'>>(
            (row) => ({
              id: row.id,
              name: row.name,
              description: row.description,
              value: row.listValue ?? row.value,
              isProtected: row.isProtected,
            }),
          ),
          {
            ...body,
            // Keep editing access as the leading group before column sorting and paging.
            sortings: [
              { field: 'isProtected', direction: 'asc', index: 0 },
              ...(body.sortings ?? [])
                .filter((sorting) => sorting.field !== 'isProtected')
                .sort((left, right) => left.index - right.index)
                .map((sorting, index) => ({ ...sorting, index: index + 1 })),
            ],
          },
        ),
      ),
    );
  }

  public getInfo(id: string) {
    return this.rows().pipe(
      map((rows) => {
        const row = rows.find((item) => item.id === id);
        if (!row) throw new Error('Параметр не найден');
        return row;
      }),
    );
  }

  public updateParam(id: string, params: ISystemParamUpdateParams) {
    return defer(() => {
      if (id !== NOTIFICATION_EVENT_AGE_PARAM_ID)
        throw new Error('Изменение параметра не поддерживается');
      if (!this.session.isSecurityAdministrator)
        throw new Error('Недостаточно прав для изменения параметра');
      if (params.expectedRevision == null)
        throw new Error('Не загружена версия системной настройки');

      return this.notifications.updateSystemSettings({
        expectedRevision: params.expectedRevision,
        maximumEventAgeSeconds: parseMaximumEventAge(params.value),
      });
    });
  }

  private rows() {
    const canManage = this.session.isSecurityAdministrator;
    return forkJoin({
      smtp: this.notifications.smtp().pipe(
        map((value) => ({ available: true as const, value })),
        catchError(() => of({ available: false as const, value: null })),
      ),
      settings: canManage
        ? this.notifications.systemSettings().pipe(
            map((value) => ({ available: true as const, value })),
            catchError(() => of({ available: false as const, value: null })),
          )
        : of({ available: false as const, value: null }),
    }).pipe(
      map(({ smtp, settings }): ISystemParam[] => [
        ...smtpConfigurationParams(smtp),
        notificationEventAgeParam(settings, canManage),
      ]),
    );
  }
}
